'use client';

/**
 * SEC-003 Phase 2b (I2.2) + Interim — the one place the browser shows an account / feature notice
 * raised by authFetch:
 *   - AUTH_NOTICE_EVENT       (403 PROFILE_NOT_AUTHORIZED): "Your account is not set up for this…"
 *     — valid sign-in, but no profile, no organization, or no usable role. The session is kept.
 *   - PHI_NOT_ENABLED_EVENT   (503 PHI_NOT_ENABLED): "Patient features are not enabled yet…" —
 *     a PHI call reached a route while there is no PHI database (a race, or a screen that does
 *     not check usePHIStatus first). Never raw JSON.
 * The page that made the call still gets its response and handles it as before. Mounted once in
 * the root layout, in the alert layer (UI-004) so it stays visible above an open modal.
 * Dismissable; shown again on the next refusal.
 */

import { useEffect, useState } from 'react';
import AlertLayer from '@/components/ui/AlertLayer';
import {
  AUTH_NOTICE_EVENT,
  PHI_NOT_ENABLED_EVENT,
  PHI_NOT_ENABLED_NOTICE,
  PROFILE_NOT_AUTHORIZED_MESSAGE,
} from '@/lib/auth/session-policy';

export default function AuthNoticeBanner() {
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    const profile = () => setMessage(PROFILE_NOT_AUTHORIZED_MESSAGE);
    const phi = () => setMessage(PHI_NOT_ENABLED_NOTICE);
    window.addEventListener(AUTH_NOTICE_EVENT, profile);
    window.addEventListener(PHI_NOT_ENABLED_EVENT, phi);
    return () => {
      window.removeEventListener(AUTH_NOTICE_EVENT, profile);
      window.removeEventListener(PHI_NOT_ENABLED_EVENT, phi);
    };
  }, []);

  if (!message) return null;

  return (
    <AlertLayer>
      <div
        role="alert"
        data-testid="auth-notice-banner"
        className="fixed top-3 left-1/2 -translate-x-1/2 z-alert w-[min(36rem,calc(100%-2rem))] alert alert-warning shadow-lg py-3 text-sm"
      >
        <span>{message}</span>
        <button
          type="button"
          className="btn btn-ghost btn-xs"
          aria-label="Dismiss"
          onClick={() => setMessage(null)}
        >
          ✕
        </button>
      </div>
    </AlertLayer>
  );
}
